import { Component, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { clearVitePreloadErrorReloadGuard } from '../preloadErrorRecovery';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  resetKey: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

export function RouteErrorFallback({ onReload }: { onReload: () => void }) {
  return (
    <div className="p-4 rounded-lg bg-red-900/20 border border-red-600/50 text-red-400 flex items-center justify-between gap-4">
      <span>This page could not be loaded after the app updated.</span>
      <button
        type="button"
        onClick={onReload}
        className="text-red-400 hover:text-red-300 font-semibold"
      >
        Reload
      </button>
    </div>
  );
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(previousProps: RouteErrorBoundaryProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}

export function RouteErrorBoundaryWithLocation({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();

  return (
    <RouteErrorBoundary resetKey={location.key}>{children}</RouteErrorBoundary>
  );
}

export function RouteLoadSuccess({ children }: { children: ReactNode }) {
  useEffect(() => {
    clearVitePreloadErrorReloadGuard();
  }, []);

  return <>{children}</>;
}
